import { useSessionStore } from '@/entities/Session/model'
import { useUserStore } from '@/stores/useUser'
import { useFetch } from '@/shared/api/fetch/useFetch'
import { VALIDATE_TELEGRAM_DATA_URL } from '@/shared/api'
import { getTelegramInitData } from './telegramAuth'
import type { AuthResponse } from '@/shared/repositories/AuthRepository'
import type { UserInfo } from '@/entities/User/model/types'

/**
 * Интерфейс для обновления токена
 */
export interface RefreshTokenResponse {
    token: string;
    user?: UserInfo;
}

/**
 * Обновляет токен доступа через повторную авторизацию Telegram
 */
export async function refreshToken(): Promise<boolean> {
    try {
        const sessionStore = useSessionStore()
        const userStore = useUserStore()
        
        console.log('Попытка обновления токена через Telegram...')
        
        // Получаем данные Telegram для повторной авторизации
        const { initData } = getTelegramInitData()
        
        if (!initData) {
            console.error('Нет данных Telegram для обновления токена')
            return false
        }

        // Делаем запрос на авторизацию (аналогично первичной авторизации)
        const response = await useFetch<AuthResponse, { initData: string }>({
            method: 'POST',
            resource: VALIDATE_TELEGRAM_DATA_URL,
            body: { initData },
            skipAuthErrorHandling: true, // Отключаем автоматическую обработку для этого запроса
            adapter: (data) => data,
        })
        
        // Обновляем токен и данные пользователя
        sessionStore.setToken(response.data.token)
        userStore.setUser(response.data.user)

        console.log('Токен успешно обновлен через Telegram авторизацию')
        return true
        
    } catch (error) {
        console.error('Ошибка обновления токена через Telegram:', error)
        
        // Если не удалось обновить токен, очищаем данные
        const sessionStore = useSessionStore()
        const userStore = useUserStore()
        sessionStore.clearToken()
        userStore.clearUser()
        
        return false
    }
}

/**
 * Проверяет не истек ли токен на основе его содержимого (JWT)
 * Примечание: Работает только если токен в формате JWT
 */
export function isTokenExpired(token: string): boolean {
    try {
        // Для JWT токенов
        const payload = JSON.parse(atob(token.split('.')[1]))
        const currentTime = Math.floor(Date.now() / 1000)
        
        return payload.exp && payload.exp < currentTime
    } catch (error) {
        // Если не удалось распарсить токен, считаем его невалидным
        console.warn('Не удалось проверить срок действия токена:', error)
        return true
    }
}

/**
 * Автоматически проверяет и обновляет токен при необходимости
 */
export async function ensureValidToken(): Promise<boolean> {
    const sessionStore = useSessionStore()
    
    if (!sessionStore.token) {
        return false
    }
    
    // Если токен истек, пытаемся его обновить
    if (isTokenExpired(sessionStore.token)) {
        console.log('Токен истек, пытаемся обновить...')
        return await refreshToken()
    }
    
    return true
}

/**
 * Принудительно обновляет токен даже если он еще не истек
 * Полезно для превентивного обновления токенов
 */
export async function forceRefreshToken(): Promise<boolean> {
    console.log('Принудительное обновление токена...')
    return await refreshToken()
}

/**
 * Проверяет истекает ли токен в ближайшее время (в секундах)
 * Полезно для превентивного обновления
 */
export function isTokenExpiringSoon(token: string, thresholdSeconds: number = 300): boolean {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        const currentTime = Math.floor(Date.now() / 1000)
        
        return payload.exp && (payload.exp - currentTime) < thresholdSeconds
    } catch (error) {
        console.warn('Не удалось проверить время истечения токена:', error)
        return true
    }
}
